import { Box, Flex, Theme } from '@radix-ui/themes';
import { useMemo } from 'react';
import { Link, useLocation, useOutlet } from 'react-router-dom';
import AcademicWork from './AcademicWork';
import SectionPreviews from './SectionPreviews';

type TabDef = { slug: string; label: string; to: string; color: string };

// "academic-work" is the index of /portfolio, so its URL is /portfolio
// (not /portfolio/academic-work). Each other tab gets its own /portfolio/<slug>.
const TABS: TabDef[] = [
    { slug: 'academic-work', label: 'ACADEMIC WORK', to: '/portfolio', color: '#E6BE16' },
    { slug: 'storenvy', label: 'STORENVY', to: '/portfolio/storenvy', color: '#78C8A7' },
    { slug: 'little-red-hen', label: 'LITTLE RED HEN', to: '/portfolio/little-red-hen', color: '#B42029' },
    {
        slug: 'engelbrecht-advertising',
        label: 'ENGELBRECHT ADVERTISING',
        to: '/portfolio/engelbrecht-advertising',
        color: '#209EB4',
    },
];

function activeSlugFromPath(pathname: string): string {
    // /portfolio -> academic-work, /portfolio/storenvy -> storenvy
    const segs = pathname.replace(/\/+$/, '').split('/').filter(Boolean);
    if (segs[0] !== 'portfolio') return 'academic-work';
    return segs[1] ?? 'academic-work';
}

function Portfolio() {
    const { pathname } = useLocation();
    const activeSlug = activeSlugFromPath(pathname);
    // At `/` there's no nested route, so render AcademicWork directly.
    const outlet = useOutlet();

    const [gradientClass, backgroundColor] = useMemo(() => {
        const gradientTo = TABS.find((t) => t.slug === activeSlug)?.color ?? '#E6BE16';
        return [`bg-linear-to-b from-[#000000] from-0% to-[${gradientTo}] to-80%`, gradientTo];
    }, [activeSlug]);

    return (
        <Flex
            justify="center"
            position="absolute"
            px="5"
            style={{ top: 100, bottom: 0, backgroundColor, width: '100%', paddingTop: 10 }}
            className={gradientClass}
        >
            <Flex direction="column" flexGrow="1" width="100%" mx="auto" style={{ maxWidth: 1536, minHeight: 0 }}>
                <Flex
                    asChild
                    aria-label="portfolio views"
                    className="portfolio-tabs"
                    style={{ height: 80, flexShrink: 0, overflowX: 'auto' }}
                >
                    <nav>
                        {TABS.map((t) => (
                            <Link
                                key={t.slug}
                                to={t.to}
                                className="portfolio-tab"
                                data-state={t.slug === activeSlug ? 'active' : 'inactive'}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: 80,
                                    padding: '0 16px',
                                    fontSize: 24,
                                    color: '#ffffff',
                                    textDecoration: 'none',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {t.label}
                            </Link>
                        ))}
                    </nav>
                </Flex>

                <Box position="relative" style={{ flexShrink: 0, zIndex: 10 }}>
                    <Box
                        width="100%"
                        position="absolute"
                        style={{ height: 40 }}
                        className="h-60 w-full bg-linear-to-b from-white to-transparent"
                    />
                </Box>
                <Box
                    style={{
                        backgroundColor: '#ffffff',
                        overflowY: 'auto',
                        overscrollBehavior: 'none',
                        width: '100%',
                        flexGrow: 1,
                        minHeight: 0,
                    }}
                >
                    <Theme appearance="light" hasBackground={false}>
                        {outlet ?? <AcademicWork />}
                        {activeSlug === 'academic-work' && <SectionPreviews />}
                    </Theme>
                </Box>
            </Flex>
        </Flex>
    );
}

export default Portfolio;
